import {MouseEvent, ReactNode} from 'react'
import axios, {AxiosError} from 'axios'
import {Link} from 'react-router-dom'
import cn from 'clsx'
import {Space} from 'antd'
import useStore from 'base/store'
import './File.scss'
import {
  Box,
  Text,
  IconButton,
  FileIcon,
  DownloadIcon,
  CloseSecondIcon,
} from 'common/components'
import {formatDate, showErrorNotification} from 'common/helpers'

export interface FileProps {
  uuid?: string
  className?: string
  to?: string
  bordered?: boolean
  secondary?: boolean
  highlightedName?: ReactNode
  name: string
  size: number
  url: string
  userName?: string
  updatedAt?: string
  createdAt?: string
  drawdownName?: string
  menu?: ReactNode
  isRemoveLoading?: boolean
  onRemove?: (uuid?: string) => void
}

interface RemoveProps {
  uuid: string
  isRemoveLoading?: boolean
  onRemove: (uuid: string) => void
}

export default function File({
  uuid,
  className,
  to,
  bordered,
  secondary,
  highlightedName,
  name,
  size,
  url,
  userName,
  updatedAt,
  createdAt,
  drawdownName,
  menu,
  isRemoveLoading,
  onRemove,
}: FileProps) {
  const role = useStore((state) => state.role)
  const handleDownload = async (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      const {data} = await axios.get(url, {
        responseType: 'blob',
      })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(data)
      link.download = name
      link.click()
    } catch (error) {
      showErrorNotification((error as AxiosError).message)
    }
  }

  const fileClassNames = cn(
    className,
    'File',
    isRemoveLoading && 'File_removing',
    bordered && 'File_bordered',
    secondary && 'File_secondary'
  )

  const content = (
    <>
      <Space size={12}>
        <FileIcon />
        <div>
          <p className="File-Name">{highlightedName ?? name}</p>
          <Text size="2xs" color="grey">
            {drawdownName && (
              <>
                {drawdownName}
                <Spacer />
              </>
            )}
            {createdAt && (
              <>
                {formatDate(createdAt, 'fifth')}
                <Spacer />
              </>
            )}
            {Math.round(size / 1000)} Kb
            {updatedAt && (
              <>
                <Spacer />
                Updated {formatDate(updatedAt, 'sixth')}
              </>
            )}
            {userName && (
              <>
                <Spacer />
                {userName}
              </>
            )}
          </Text>
        </div>
      </Space>
      <div className="File-Actions">
        { (!role?.user) && menu}
        <IconButton
          className="File-Action"
          icon={<DownloadIcon />}
          onClick={handleDownload}
        />
        {onRemove && (
          <Remove
            uuid={uuid}
            isRemoveLoading={isRemoveLoading}
            onRemove={onRemove}
          />
        )}
      </div>
    </>
  )

  if (to)
    return (
      <Link className={fileClassNames} to={to}>
        {content}
      </Link>
    )

  const isBase64URL = url.startsWith('data:')

  return (
    <a
      download={isBase64URL ? name : undefined}
      className={fileClassNames}
      href={url}
      target="_blank"
      rel="noreferrer noopener"
    >
      {content}
    </a>
  )
}

const Remove = ({uuid, isRemoveLoading, onRemove}: RemoveProps) => {
  const handleClick = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onRemove(uuid)
  }

  return (
    <IconButton
      className="File-Action"
      icon={<CloseSecondIcon />}
      loading={isRemoveLoading}
      onClick={handleClick}
    />
  )
}

const Spacer = () => (
  <Box as="span" ml={6} mr={6}>
    •
  </Box>
)
